// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { DataBundle } from './types';
import { loadData, indexFacilities, indexSubstances, indexTransfers } from './data';
import { el, icon, clear } from './dom';
import { installGlossaryHandlers } from './glossary';
import { renderOverview } from './views/overview';
import { renderMap } from './views/map';
import { renderSearch } from './views/search';
import { renderSubstances } from './views/substances';
import { renderIndustries } from './views/industries';
import { renderFlow } from './views/flow';
import { renderStates } from './views/states';
import { renderTrends } from './views/trends';
import { renderInsights } from './views/insights';
import { openAbout } from './about';
import { openFacility } from './views/facility-detail';

interface View {
  id: string;
  label: string;
  render: (root: HTMLElement, ctx: AppContext) => void;
}

export interface AppContext {
  data: DataBundle;
  facilitiesById: Map<number, DataBundle['facilities'][number]>;
  substancesById: Map<number, DataBundle['substances'][number]>;
  transfersById: Map<number, DataBundle['transfers'][number]>;
  navigate: (viewId: string, params?: Record<string, string>) => void;
  openFacility: (id: number) => void;
}

const VIEWS: View[] = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'search', label: 'Search', render: renderSearch },
  { id: 'substances', label: 'Substances', render: renderSubstances },
  { id: 'industries', label: 'Industries', render: renderIndustries },
  { id: 'flow', label: 'Flow', render: renderFlow },
  { id: 'states', label: 'States', render: renderStates },
  { id: 'trends', label: 'Trends', render: renderTrends },
  { id: 'insights', label: 'Insights', render: renderInsights }
];

export async function mount(root: HTMLElement) {
  // Loading state
  clear(root);
  root.appendChild(buildSkeleton());
  showLoading(getMainEl());

  let data: DataBundle;
  try {
    data = await loadData();
  } catch (err) {
    showError(getMainEl(), err);
    return;
  }

  const facilitiesById = indexFacilities(data.facilities);
  const substancesById = indexSubstances(data.substances);
  const transfersById = indexTransfers(data.transfers);

  let activeView = 'overview';

  const navigate = (viewId: string, params?: Record<string, string>) => {
    activeView = viewId;
    const hash = '#' + viewId + (params ? '?' + new URLSearchParams(params).toString() : '');
    history.replaceState(null, '', hash);
    render();
  };

  const ctx: AppContext = {
    data,
    facilitiesById,
    substancesById,
    transfersById,
    navigate,
    openFacility: (id) => openFacility(id, ctx)
  };

  function render() {
    // Refresh nav
    const tabs = root.querySelectorAll<HTMLElement>('.tab');
    tabs.forEach(t => {
      t.classList.toggle('active', t.dataset.view === activeView);
    });
    const main = getMainEl();
    clear(main);
    const view = VIEWS.find(v => v.id === activeView) ?? VIEWS[0];
    try {
      view.render(main, ctx);
    } catch (err) {
      showError(main, err);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function getMainEl(): HTMLElement {
    return root.querySelector<HTMLElement>('.main-content')!;
  }

  // Header
  const header = root.querySelector<HTMLElement>('.site-header')!;
  clear(header);
  header.appendChild(buildHeader(VIEWS, () => navigate('about-modal'), navigate));

  installGlossaryHandlers();

  // Hash-based router (#view or #view?key=val)
  window.addEventListener('hashchange', applyHash);
  applyHash();

  function applyHash() {
    const hash = window.location.hash.replace(/^#/, '');
    const [viewId, query] = hash.split('?');
    if (viewId === 'about-modal') {
      openAbout();
      return;
    }
    if (viewId === 'facility' && query) {
      const params = new URLSearchParams(query);
      const id = Number(params.get('id'));
      if (Number.isFinite(id)) ctx.openFacility(id);
      return;
    }
    if (viewId && VIEWS.some(v => v.id === viewId)) {
      activeView = viewId;
    }
    render();
  }

  // Listen for "open facility" custom events.
  document.addEventListener('open-facility', ((ev: Event) => {
    const ce = ev as CustomEvent<{ id: number }>;
    ctx.openFacility(ce.detail.id);
  }) as EventListener);
}

function buildSkeleton(): DocumentFragment {
  const frag = document.createDocumentFragment();
  const header = el('header', { class: 'site-header' });
  const main = el('main', { class: 'main-content' });
  const footer = el('footer', { class: 'site-footer' }, [
    el('p', { html: 'Data: <a href="https://www.dcceew.gov.au/environment/protection/npi" target="_blank" rel="noopener">National Pollutant Inventory</a>, DCCEEW, via <a href="https://data.gov.au/data/dataset/043f58e0-a188-4458-b61c-04e5b540aea4" target="_blank" rel="noopener">data.gov.au</a>. Always check the <a href="http://www.npi.gov.au/" target="_blank" rel="noopener">official register</a> for compliance use.' }),
    el('p', { html: 'Built by <a href="https://benrichardson.dev/">benrichardson.dev</a>.' })
  ]);
  frag.appendChild(header);
  frag.appendChild(main);
  frag.appendChild(footer);
  return frag;
}

function buildHeader(views: View[], openAboutModal: () => void, navigate: (id: string) => void): HTMLElement {
  const tabs = el('nav', { class: 'tabs', role: 'tablist' },
    views.map(v => {
      const t = el('button', {
        class: 'tab',
        'data-view': v.id,
        role: 'tab',
        onclick: () => navigate(v.id)
      }, v.label);
      return t;
    })
  );

  const aboutBtn = el('button', {
    class: 'header-action',
    'aria-label': 'About this site',
    title: 'About & glossary',
    onclick: openAboutModal
  }, [icon('info')]);

  return el('div', { class: 'header-inner' }, [
    el('div', { class: 'brand' }, [
      el('div', { class: 'brand-logo', 'aria-hidden': 'true' }, [icon('leaf', 20)]),
      el('div', null, [
        el('div', { class: 'brand-name' }, 'Pollution Watch'),
        el('div', { class: 'brand-sub' }, 'National Pollutant Inventory · AU')
      ])
    ]),
    tabs,
    el('div', { class: 'header-actions' }, [aboutBtn])
  ]);
}

function showLoading(main: HTMLElement) {
  clear(main);
  main.appendChild(el('div', { class: 'loading' }, [
    el('div', { class: 'loading-spinner', 'aria-hidden': 'true' }),
    el('p', null, 'Loading the National Pollutant Inventory …'),
    el('p', { class: 'text-tertiary' }, 'About 8 MB of data on the first load — then cached.')
  ]));
}

function showError(main: HTMLElement, err: unknown) {
  clear(main);
  const message = err instanceof Error ? err.message : String(err);
  main.appendChild(el('div', { class: 'error' }, [
    el('strong', null, 'Something went wrong.'),
    el('p', null, message),
    el('button', { class: 'btn primary mt-lg', onclick: () => location.reload() }, 'Reload')
  ]));
}
